import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { taka, formatApiErrorDetail } from "@/lib/api";
import { Modal, Field, inputCls, Th, Td, Empty } from "@/pages/admin/AdminUI";

const UNITS = ["কেজি", "বস্তা", "টন", "মণ", "পিস"];
const INV_KEYS = [["rice", "চাল"], ["paddy", "ধান"], ["broken", "খুদ"], ["bran", "ব্রান"], ["husk", "তুষ"], ["powder", "গুঁড়া"], ["other", "অন্যান্য"]];
const CATS = [["rice", "চাল"], ["paddy", "ধান"], ["broken", "খুদ"], ["bran", "রাইস ব্রান"], ["husk", "তুষ"], ["powder", "গুঁড়া"], ["other", "অন্যান্য"]];

const empty = {
  name_bn: "", name_en: "", category: "rice", description: "", image: "",
  price: 0, wholesale_price: 0, retail_price: 0, unit: "কেজি", stock: 0,
  min_stock: 0, featured: false, active: true, inventory_key: "rice", bulk_tiers: [],
  delivery_charge: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/products", { params: { include_inactive: true } }).then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const onUploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("ছবি আপলোড হয়েছে");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    const payload = { ...form };
    ["price", "wholesale_price", "retail_price", "stock", "min_stock"].forEach((k) => payload[k] = Number(payload[k]) || 0);
    payload.delivery_charge = (form.delivery_charge === "" || form.delivery_charge === null || form.delivery_charge === undefined) ? null : Number(form.delivery_charge);
    payload.bulk_tiers = (form.bulk_tiers || []).filter((t) => t.min_qty && t.price).map((t) => ({ min_qty: Number(t.min_qty), price: Number(t.price) }));
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("সংরক্ষিত হয়েছে");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("এই পণ্যটি মুছবেন?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("মুছে ফেলা হয়েছে"); load();
  };

  const filtered = products.filter((p) => p.name_bn.includes(q) || p.name_en.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#1E3A2B]">পণ্য ব্যবস্থাপনা</h1>
        <button onClick={openNew} className="bg-[#1E3A2B] text-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="add-product-btn"><Plus size={18} /> নতুন পণ্য</button>
      </div>

      <div className="relative max-w-sm">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="পণ্য খুঁজুন..." className={`${inputCls} pl-9`} data-testid="product-search" />
        <Search size={16} className="absolute left-3 top-3.5 text-stone-400" />
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr><Th>পণ্য</Th><Th>ক্যাটাগরি</Th><Th>দাম</Th><Th>স্টক</Th><Th>স্ট্যাটাস</Th><Th></Th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`}>
                <Td>
                  <div className="flex items-center gap-2">
                    <img src={p.image} className="w-10 h-10 rounded-lg object-cover" alt="" />
                    <div><div className="font-medium text-stone-800">{p.name_bn}</div><div className="text-xs text-stone-400">{p.name_en}</div></div>
                  </div>
                </Td>
                <Td>{CATS.find((c) => c[0] === p.category)?.[1] || p.category}</Td>
                <Td className="font-semibold text-emerald-800">{taka(p.price)}/{p.unit}</Td>
                <Td><span className={p.stock <= p.min_stock ? "text-amber-600 font-semibold" : ""}>{Math.round(p.stock)}</span></Td>
                <Td>
                  <div className="flex gap-1">
                    {p.active ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">সক্রিয়</span> : <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full">নিষ্ক্রিয়</span>}
                    {p.featured && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">ফিচার্ড</span>}
                  </div>
                </Td>
                <Td>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(p)} className="p-2 hover:bg-stone-100 rounded-lg text-blue-600" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                    <button onClick={() => del(p.id)} className="p-2 hover:bg-stone-100 rounded-lg text-red-600" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <Empty text="কোনো পণ্য নেই" />}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "পণ্য সম্পাদনা" : "নতুন পণ্য"} wide>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="নাম (বাংলা)"><input className={inputCls} value={form.name_bn} onChange={(e) => set("name_bn", e.target.value)} data-testid="pf-name-bn" /></Field>
          <Field label="নাম (English)"><input className={inputCls} value={form.name_en} onChange={(e) => set("name_en", e.target.value)} data-testid="pf-name-en" /></Field>
          <Field label="ক্যাটাগরি"><select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category">{CATS.map((c) => <option key={c[0]} value={c[0]}>{c[1]}</option>)}</select></Field>
          <Field label="ইনভেন্টরি টাইপ"><select className={inputCls} value={form.inventory_key} onChange={(e) => set("inventory_key", e.target.value)} data-testid="pf-invkey">{INV_KEYS.map((c) => <option key={c[0]} value={c[0]}>{c[1]}</option>)}</select></Field>
          <div className="sm:col-span-2">
            <Field label="পণ্যের ছবি">
              <div className="flex items-start gap-3">
                {form.image ? (
                  <img src={form.image} alt="preview" className="w-20 h-20 rounded-xl object-cover border border-stone-200 shrink-0" data-testid="pf-image-preview" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-400 text-xs shrink-0">নেই</div>
                )}
                <div className="flex-1 space-y-2">
                  <label className={`inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#1E3A2B] text-white text-sm font-medium cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="pf-image-upload-label">
                    <Upload size={16} /> {uploading ? "আপলোড হচ্ছে..." : "ছবি আপলোড করুন"}
                    <input type="file" accept="image/*" className="hidden" onChange={onUploadImage} data-testid="pf-image-file" />
                  </label>
                  <input className={inputCls} value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="অথবা ছবির URL দিন" data-testid="pf-image" />
                </div>
              </div>
            </Field>
          </div>
          <Field label="একক"><select className={inputCls} value={form.unit} onChange={(e) => set("unit", e.target.value)} data-testid="pf-unit">{UNITS.map((u) => <option key={u}>{u}</option>)}</select></Field>
          <Field label="বিক্রয় মূল্য (৳)"><input type="number" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" /></Field>
          <Field label="খুচরা মূল্য (৳)"><input type="number" className={inputCls} value={form.retail_price} onChange={(e) => set("retail_price", e.target.value)} /></Field>
          <Field label="পাইকারি মূল্য (৳)"><input type="number" className={inputCls} value={form.wholesale_price} onChange={(e) => set("wholesale_price", e.target.value)} /></Field>
          <Field label="স্টক"><input type="number" className={inputCls} value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="pf-stock" /></Field>
          <Field label="সর্বনিম্ন স্টক সতর্কতা"><input type="number" className={inputCls} value={form.min_stock} onChange={(e) => set("min_stock", e.target.value)} data-testid="pf-minstock" /></Field>
          <Field label="ডেলিভারি চার্জ (৳ — খালি রাখলে গ্লোবাল প্রযোজ্য)"><input type="number" className={inputCls} value={form.delivery_charge ?? ""} onChange={(e) => set("delivery_charge", e.target.value)} data-testid="pf-delivery-charge" placeholder="গ্লোবাল" /></Field>
          <Field label="বিবরণ"><textarea className={`${inputCls} h-20 py-2`} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <div className="flex items-center gap-4 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="pf-featured" /> ফিচার্ড</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="pf-active" /> সক্রিয়</label>
          </div>
          <div className="sm:col-span-2 border-t border-stone-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#1E3A2B]">পরিমাণভিত্তিক পাইকারি দাম (বাল্ক টায়ার)</span>
              <button type="button" onClick={() => set("bulk_tiers", [...(form.bulk_tiers || []), { min_qty: "", price: "" }])} className="text-xs text-emerald-700 font-medium" data-testid="add-bulk-tier">+ টায়ার যোগ</button>
            </div>
            {(form.bulk_tiers || []).map((t, i) => (
              <div key={i} className="flex items-center gap-2 mb-2" data-testid={`bulk-tier-${i}`}>
                <input type="number" placeholder="সর্বনিম্ন পরিমাণ" className={inputCls} value={t.min_qty} onChange={(e) => set("bulk_tiers", form.bulk_tiers.map((x, idx) => idx === i ? { ...x, min_qty: e.target.value } : x))} data-testid={`tier-minqty-${i}`} />
                <input type="number" placeholder="দাম (৳)" className={inputCls} value={t.price} onChange={(e) => set("bulk_tiers", form.bulk_tiers.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x))} data-testid={`tier-price-${i}`} />
                <button type="button" onClick={() => set("bulk_tiers", form.bulk_tiers.filter((_, idx) => idx !== i))} className="text-red-500 shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
            {(form.bulk_tiers || []).length === 0 && <div className="text-xs text-stone-400">যেমন: ৫০+ কেজিতে ৳৬৮ — একাধিক টায়ার যোগ করা যায়</div>}
          </div>
        </div>
        <button onClick={save} className="mt-4 w-full bg-[#1E3A2B] text-white h-11 rounded-xl font-medium" data-testid="save-product-btn">সংরক্ষণ করুন</button>
      </Modal>
    </div>
  );
}
